import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChangeOnePart } from 'amira-intervention-library';
import useDidAttempt from '../../hooks/useDidAttempt';
import usePauseControl from '../../hooks/usePauseControl';
import useMaxListenTimer from '../../hooks/useMaxListenTimer';
import useEndingSpeech from '../../hooks/useEndingSpeech';
import get from 'lodash.get';
import { COPY_STRINGS } from '../../../../../constants/translatedCopy';
import { getUserData } from '../../../../../services/API';

export default function ChangeOnePartTutor({ metadata,
    transcript,
    paused,
    setWillHandlePause,
    startPhrase,
    pausePhrase,
    resumePhrase,
    doneHandlingPause,
    isInstruction,
    hasInstructions,
    isFTUE,
    currentWordIndex,
    setDisableInterventionsForPhrase,
    setAllowFirstWordSkipOnPause = () => {}, }) {

    const { shouldHandlePause, finishHandlePause, passPauseControlBackToReaderManager } = usePauseControl({
        paused,
        setWillHandlePause,
        pausePhrase,
        resumePhrase,
        doneHandlingPause,
        transcript
    })

    const { didAttempt } = useDidAttempt({ transcript })

    const { replaceTheEndingSequence } = useEndingSpeech()
    const { maxListenTimerEnded, startMaxListenTimer } = useMaxListenTimer()

    useEffect(() => {
        setDisableInterventionsForPhrase(true)
    }, [])

    useEffect(() => {
        if (didAttempt) {
            passPauseControlBackToReaderManager();
        }
    }, [didAttempt])

    /**
     * provide end of phrase feedback on correct, incorrect and silence
     */
    useEffect(() => {
        // only calculate feedback if the word index has moved
        if(currentWordIndex === 0) return

        // if word index moved before the listen timer ends, then it is correct.
        if (!maxListenTimerEnded) {
            // correct
            replaceTheEndingSequence([{
                textToSpeak: COPY_STRINGS.intervention_strings.change_one_part.correct,
            }]);
        } else if (!didAttempt) {
            // silence
            const word = get(metadata, "wordMetadata.SUBS[0].word", "")
            replaceTheEndingSequence([
                {
                    textToSpeak: COPY_STRINGS.intervention_strings.change_one_part.tutor_silent_end,
                }, 
                {
                    textToSpeak: word,
                    skipTranslation: getUserData().bilingualMode,
                }
            ]);
        }

        // only run this effect when the word index changes because we only want to 
        // calculate the feedback to give when the word is finished.
    }, [currentWordIndex])

    /**
     * prepare metadata for tutor
     */
    const tutorMetadata = {
        ...metadata,
        isInstruction,
        hasInstructions,
        isFTUE,
        handleMessage: (message) => { // basically gives it the full control of the phrase timing
            const { event } = message
            if (event === "start") {
                // after intro
                // GT-400: with interventions off, a first word of a story that is never recognized
                // has no exit, so let ReaderManager force the skip past its first-word guard. Opt in
                // only now, once listening starts, so the intro can never be skipped through.
                setAllowFirstWordSkipOnPause(true)
                startPhrase()
                startMaxListenTimer()
            } else if (event === "retry") {
                // setDisableInterventionsForPhrase(false) // uncomment this when we have change one part intervention disabled for this type.
                // clear the phrase timeout so that we can speak without the clock ticking and move to the next phrase during the speech
                finishHandlePause();
                passPauseControlBackToReaderManager()
            }
        }
    }

    const states = {
        paused: paused && shouldHandlePause,
    }

    return <ChangeOnePart {...tutorMetadata} {...states} />
}

ChangeOnePartTutor.propTypes = {
    metadata: PropTypes.object.isRequired,
    startPhrase: PropTypes.func.isRequired,
    isFTUE: PropTypes.bool,
    currentWordIndex: PropTypes.number.isRequired,
    transcript: PropTypes.object.isRequired,
    paused: PropTypes.bool.isRequired,
    setWillHandlePause: PropTypes.func.isRequired,
    pausePhrase: PropTypes.func.isRequired,
    resumePhrase: PropTypes.func.isRequired,
    doneHandlingPause: PropTypes.func.isRequired,
    isInstruction: PropTypes.bool.isRequired,
    hasInstructions: PropTypes.bool.isRequired,
    setDisableInterventionsForPhrase: PropTypes.func.isRequired,
    setAllowFirstWordSkipOnPause: PropTypes.func,
}